import { DialogComponent } from '@theme/dialog';

/**
 * Shared across the (single) museum instance: caches fetched drop pages by URL.
 * Stores the in-flight promise so concurrent requests de-dupe, mirroring
 * assets/section-renderer.js.
 * @type {Map<string, Promise<string>>}
 */
const PAGE_CACHE = new Map();

/**
 * [WH] Museum — drops archive + seamless overlay viewer.
 *
 * The grid (server-rendered) is the base layer. Opening the overlay shows a
 * header band — a filmstrip of thumbnails (active centered, neighbours peeking)
 * + the drop's metadata — rendered entirely client-side from the config JSON.
 * Below it, the drop's OWN storefront page is fetched and its `#MainContent`
 * sections are lifted in — no page reload.
 */
class WhMuseum extends DialogComponent {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.whBound === '1') return;
    this.dataset.whBound = '1';

    const cfgEl = this.querySelector('script[data-wh-museum-config]');
    if (!cfgEl) return; // empty museum (no drops)

    let config;
    try {
      config = JSON.parse(cfgEl.textContent);
    } catch (e) {
      console.error('[wh-museum] Failed to parse config', e);
      return;
    }
    if (!Array.isArray(config) || config.length === 0) return;

    this.byHandle = new Map(config.map((d) => [d.handle, d]));
    // Authoritative display order: newest first by the emitted release date.
    this.ordered = config.slice().sort((a, b) => {
      if (a.sort < b.sort) return 1;
      if (a.sort > b.sort) return -1;
      return 0;
    });
    this.currentIndex = 0;
    this.pendingIndex = null;
    this.loadToken = 0;

    this.labels = {
      studio: this.dataset.labelStudio || 'artist',
      model: this.dataset.labelModel || 'model',
      date: this.dataset.labelDate || 'date',
    };

    // Click-outside guard (CLAUDE.md pitfall #26): swapping viewer content on
    // arrow-nav detaches the clicked node mid-bubble, which DialogComponent
    // would misread as a backdrop click and close. Stop in-dialog clicks at the
    // dialog level; genuine backdrop clicks (target === dialog) still bubble.
    const dialogEl = this.refs.dialog;
    if (dialogEl) {
      dialogEl.addEventListener('click', (e) => {
        if (e.target !== dialogEl) e.stopPropagation();
      });
    }

    this.grid = this.querySelector('.wh-museum__grid');
    if (this.grid) this.grid.addEventListener('click', this.#onGridClick);
    if (this.refs.prev) this.refs.prev.addEventListener('click', this.#onPrev);
    if (this.refs.next) this.refs.next.addEventListener('click', this.#onNext);
    if (this.refs.seeAll) this.refs.seeAll.addEventListener('click', this.#onSeeAll);
    this.addEventListener('dialog:open', this.#onDialogOpen);
    window.addEventListener('resize', this.#onResize);

    this.#buildFilmstrip();
    this.#runBlurUp(this); // grid thumbnails + filmstrip frames

    // Auto-open the latest drop on the storefront (not in the theme editor, so
    // the merchant can configure the grid without a trapping modal popping up).
    if (this.dataset.designMode !== 'true') {
      this.openViewer(this.ordered[0].handle);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.grid) this.grid.removeEventListener('click', this.#onGridClick);
    if (this.refs.prev) this.refs.prev.removeEventListener('click', this.#onPrev);
    if (this.refs.next) this.refs.next.removeEventListener('click', this.#onNext);
    if (this.refs.seeAll) this.refs.seeAll.removeEventListener('click', this.#onSeeAll);
    this.removeEventListener('dialog:open', this.#onDialogOpen);
    window.removeEventListener('resize', this.#onResize);
  }

  /* ---------------- Filmstrip ---------------- */

  #buildFilmstrip() {
    const track = this.refs.track;
    if (!track) return;
    const frag = document.createDocumentFragment();
    this.ordered.forEach((d, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wh-museum__frame';
      btn.dataset.nav = String(i);
      btn.setAttribute('aria-label', d.title || '');
      if (d.thumb) {
        const img = document.createElement('img');
        img.className = 'wh-museum-img';
        img.loading = 'lazy';
        img.alt = '';
        img.dataset.full = d.thumb;
        img.src = this.#tinyUrl(d.thumb);
        btn.appendChild(img);
      }
      frag.appendChild(btn);
    });
    track.appendChild(frag);
    track.addEventListener('click', this.#onTrackClick);
  }

  #centerFrame(index) {
    const track = this.refs.track;
    const frame = track?.children[index];
    if (!frame) return;
    const strip = frame.closest('.wh-museum__filmstrip');
    const stripWidth = strip ? strip.clientWidth : track.clientWidth;
    const x = stripWidth / 2 - (frame.offsetLeft + frame.offsetWidth / 2);
    track.style.transform = `translateX(${x}px)`;
    Array.from(track.children).forEach((f, i) => f.classList.toggle('is-active', i === index));
  }

  #renderMeta(index) {
    const meta = this.refs.meta;
    const d = this.ordered[index];
    if (!meta || !d) return;
    const rows = [];
    if (d.studio) rows.push(this.#spec(this.labels.studio, d.studio));
    if (d.model) rows.push(this.#spec(this.labels.model, d.model));
    if (d.date) rows.push(this.#spec(this.labels.date, d.date));

    meta.innerHTML = [
      d.title ? `<h1 class="wh-museum__title">${this.#esc(d.title)}</h1>` : '',
      d.year ? `<div class="wh-museum__year">${this.#esc(d.year)}</div>` : '',
      `<div class="wh-museum__meta-body">`,
      // description is merchant-authored rich text → injected as HTML
      `<div class="wh-museum__description">${d.description || ''}</div>`,
      rows.length ? `<dl class="wh-museum__specs">${rows.join('')}</dl>` : '',
      `</div>`,
    ].join('');
  }

  #updateNavLabels(index) {
    const prev = this.ordered[index - 1];
    const next = this.ordered[index + 1];
    if (this.refs.navPrev) this.refs.navPrev.hidden = !prev;
    if (this.refs.navNext) this.refs.navNext.hidden = !next;
    if (this.refs.prevName) this.refs.prevName.textContent = prev ? prev.title || '' : '';
    if (this.refs.nextName) this.refs.nextName.textContent = next ? next.title || '' : '';
  }

  #spec(label, value) {
    return `<dt>${this.#esc(label)}</dt><dd>${this.#esc(value)}</dd>`;
  }

  /* ---------------- Navigation ---------------- */

  /**
   * Opens the overlay (if closed) and navigates to a drop.
   * @param {string} handle
   */
  openViewer(handle) {
    const index = this.ordered.findIndex((d) => d.handle === handle);
    if (index < 0 || !this.refs.dialog) return;
    if (!this.refs.dialog.open) {
      this.pendingIndex = index; // centered once the dialog is laid out (dialog:open)
      this.showDialog();
    } else {
      this.goTo(index);
    }
  }

  /**
   * @param {number} index
   */
  goTo(index) {
    if (index < 0 || index >= this.ordered.length) return;
    this.currentIndex = index;
    this.#centerFrame(index);
    this.#renderMeta(index);
    this.#updateArrows();
    this.#updateNavLabels(index);
    this.#loadBody(this.ordered[index]);
  }

  #onDialogOpen = () => {
    if (this.pendingIndex == null) return;
    const index = this.pendingIndex;
    this.pendingIndex = null;
    // Center instantly on open (no slide), then restore the transition.
    const track = this.refs.track;
    if (track) track.style.transition = 'none';
    this.goTo(index);
    requestAnimationFrame(() => {
      if (track) track.style.transition = '';
    });
  };

  #onTrackClick = (e) => {
    const frame = e.target.closest('.wh-museum__frame');
    if (!frame) return;
    const index = Number(frame.dataset.nav);
    if (!Number.isNaN(index)) this.goTo(index);
  };

  #onPrev = () => this.goTo(this.currentIndex - 1);
  #onNext = () => this.goTo(this.currentIndex + 1);
  #onSeeAll = () => this.closeDialog();
  #onResize = () => {
    if (this.refs.dialog?.open) this.#centerFrame(this.currentIndex);
  };

  #updateArrows() {
    if (this.refs.prev) this.refs.prev.disabled = this.currentIndex <= 0;
    if (this.refs.next) this.refs.next.disabled = this.currentIndex >= this.ordered.length - 1;
  }

  /* ---------------- Body fetch + swap ---------------- */

  /**
   * Fetches a drop's page and swaps its `#MainContent` sections into the viewer.
   * @param {object} item
   */
  async #loadBody(item) {
    if (!item || !item.url) return; // no storefront URL yet (online store not enabled)

    const token = ++this.loadToken;
    this.refs.viewer?.setAttribute('data-loading', '');

    let html;
    try {
      html = await this.#fetchPage(item.url);
    } catch (e) {
      console.warn('[wh-museum] Fetch failed', item.url, e);
      this.refs.viewer?.removeAttribute('data-loading');
      return;
    }

    if (token !== this.loadToken) return; // a newer navigation superseded this one

    const nodes = this.#extractSections(html);
    this.refs.viewer?.removeAttribute('data-loading');
    if (!nodes.length) {
      console.warn('[wh-museum] No #MainContent in fetched page', item.url);
      return;
    }

    this.refs.viewer.replaceChildren(...nodes);
    this.#runBlurUp(this.refs.viewer);
    if (this.refs.scroll) this.refs.scroll.scrollTop = 0;
    this.#prefetchNeighbors();
  }

  #prefetchNeighbors() {
    [this.currentIndex - 1, this.currentIndex + 1].forEach((i) => {
      const item = this.ordered[i];
      if (item && item.url) this.#fetchPage(item.url).catch(() => {});
    });
  }

  /**
   * @param {string} url
   * @returns {Promise<string>}
   */
  #fetchPage(url) {
    const cached = PAGE_CACHE.get(url);
    if (cached) return cached;

    const promise = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .catch((e) => {
        PAGE_CACHE.delete(url); // allow a retry on next open
        throw e;
      });

    PAGE_CACHE.set(url, promise);
    return promise;
  }

  /**
   * Parses a fetched page and returns the `.shopify-section` wrappers from
   * `#MainContent` (IDs intact so scoped section CSS still matches). Images are
   * downgraded to a tiny blurred version BEFORE insertion so the browser never
   * fetches the full size first.
   * @param {string} html
   * @returns {Element[]}
   */
  #extractSections(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const main = doc.getElementById('MainContent');
    if (!main) return [];
    main.querySelectorAll('img').forEach((img) => this.#prepImage(img));
    return Array.from(main.children);
  }

  /* ---------------- Blur-up (materialize) ---------------- */

  /**
   * @param {HTMLImageElement} img
   */
  #prepImage(img) {
    if (img.dataset.full) return; // already carries a full-res source
    const full = this.#largestSrc(img) || img.getAttribute('src');
    if (!full || !this.#isShopifyImage(full)) return; // leave non-CDN images as-is
    img.dataset.full = full;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.setAttribute('src', this.#tinyUrl(full));
  }

  /**
   * Loads the full-res image off-DOM, then swaps + fades it in.
   * @param {ParentNode} root
   */
  #runBlurUp(root) {
    root.querySelectorAll('img[data-full]').forEach((img) => {
      if (img.dataset.whLoaded) return;
      img.dataset.whLoaded = '1';
      img.classList.add('wh-museum-img');
      const full = img.dataset.full;
      const loader = new Image();
      loader.onload = () => {
        img.src = full;
        img.classList.add('is-loaded');
      };
      loader.onerror = () => img.classList.add('is-loaded');
      loader.src = full;
    });
  }

  /**
   * @param {HTMLImageElement} img
   * @returns {string | null}
   */
  #largestSrc(img) {
    const srcset = img.getAttribute('srcset');
    if (!srcset) return null;
    const candidates = srcset
      .split(',')
      .map((part) => part.trim().split(/\s+/)[0])
      .filter(Boolean);
    return candidates[candidates.length - 1] || null;
  }

  /**
   * @param {string} url
   * @returns {boolean}
   */
  #isShopifyImage(url) {
    return /cdn\.shopify|\/cdn\/|shopifycdn|\/files\//.test(url);
  }

  /**
   * @param {string} url
   * @returns {string}
   */
  #tinyUrl(url) {
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('width', '40');
      u.searchParams.delete('height');
      return u.toString();
    } catch {
      return url;
    }
  }

  /* ---------------- Grid ---------------- */

  #onGridClick = (e) => {
    const card = e.target.closest('.wh-museum-card');
    if (!card || !this.contains(card)) return;
    e.preventDefault();
    const { handle } = card.dataset;
    if (handle) this.openViewer(handle);
  };

  /* ---------------- Utils ---------------- */

  /**
   * @param {*} value
   * @returns {string}
   */
  #esc(value) {
    return String(value == null ? '' : value).replace(
      /[&<>"]/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]
    );
  }
}

if (!customElements.get('wh-museum')) customElements.define('wh-museum', WhMuseum);
